import type { UsePinnedDiscrepanciesResult } from "@/hooks/use-pinned-discrepancies";
import {
	formatMessage,
	getRuleDisplayLabel,
	reconciliationResultMessages,
} from "@/services/messages";
import { ChevronDown, ChevronUp, ExternalLink, Pin } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { DiscrepancyCard } from "./DiscrepancyCard";
import { DiscrepancyMaster } from "./DiscrepancyMaster";
import { extractCodeFromGroupKey } from "./lib/group-key";
import { useDiscrepancyKbd } from "./lib/useDiscrepancyKbd";
import type { DiscrepancyCardViewModel } from "./lib/view-model";

/**
 * Master-detail контейнер (фича 024 ③ — Screen 3).
 *
 * Двухколоночный layout: master 360px + detail 1fr. Активирующая страница
 * `result.tsx` сама решает когда показывать MasterDetail vs feed
 * (auto-trigger ≥15 расхождений + ручной toggle в ResultBanner).
 *
 * Этот компонент — самодостаточный: внутри него pinned state и
 * keyboard-навигация. Снаружи получает только cards и runId.
 *
 * Sprint 5 / B1: client-side mute удалён — visibleCards = cards (фильтр
 * `severity/level/search` уже применён в `result.tsx:filters.filteredCards`).
 */
export interface DiscrepancyMasterDetailProps {
	cards: DiscrepancyCardViewModel[];
	runId?: string;
	/** Pin state — поднят в result.tsx, чтобы избежать рассинхрона с feed. */
	pinned: UsePinnedDiscrepanciesResult;
	/** Колбэк клика по drill-down ссылке. */
	onRelatedClick?: (targetId: string) => void;
}

export function DiscrepancyMasterDetail({
	cards,
	runId,
	pinned,
	onRelatedClick,
}: DiscrepancyMasterDetailProps) {
	const messages = reconciliationResultMessages.redesign;

	const [search, setSearch] = useState("");
	const [activeId, setActiveId] = useState<string | null>(cards[0]?.id ?? null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const detailScrollRef = useRef<HTMLDivElement>(null);

	// Sprint 5 / B1: alias на cards для совместимости с расчётами ниже.
	const visibleCards = cards;
	useEffect(() => {
		if (activeId === null && visibleCards.length > 0) {
			setActiveId(visibleCards[0].id);
			return;
		}
		if (activeId !== null && !visibleCards.some((c) => c.id === activeId)) {
			setActiveId(visibleCards[0]?.id ?? null);
		}
	}, [activeId, visibleCards]);

	const orderedIds = useMemo(() => visibleCards.map((c) => c.id), [visibleCards]);
	const activeIndex = useMemo(
		() => (activeId ? orderedIds.indexOf(activeId) : -1),
		[activeId, orderedIds],
	);
	const activeCard = activeId ? (visibleCards.find((c) => c.id === activeId) ?? null) : null;

	// Auto-scroll к detail-контенту при keyboard-навигации.
	useEffect(() => {
		if (activeId && detailScrollRef.current) {
			detailScrollRef.current.scrollTop = 0;
		}
	}, [activeId]);

	useDiscrepancyKbd({
		activeId,
		orderedIds,
		onActiveChange: setActiveId,
		onSearchFocus: () => searchInputRef.current?.focus(),
		onEscape: () => {
			searchInputRef.current?.blur();
		},
	});

	const code = activeCard ? extractCodeFromGroupKey(activeCard.groupKey) : "";
	const ruleLabel = activeCard?.violations[0]
		? getRuleDisplayLabel(activeCard.violations[0].ruleId)
		: "";

	const goPrev = () => {
		if (activeIndex > 0) setActiveId(orderedIds[activeIndex - 1]);
	};
	const goNext = () => {
		if (activeIndex >= 0 && activeIndex < orderedIds.length - 1)
			setActiveId(orderedIds[activeIndex + 1]);
	};

	return (
		<div
			// h-* (а не max-h-*) — фиксированная высота контейнера обязательна для
			// scroll-каскада: без неё grid-item'ы не получают constraint и `<ul flex-1
			// overflow-y-auto>` внутри master/detail не shrink'ается, а отрастает по
			// контенту, скрывая позиции под `overflow-hidden`. README §540px — guideline,
			// но calc(100vh-12rem) даёт адаптивность под viewport.
			className="grid h-[calc(100vh-12rem)] gap-0 overflow-hidden rounded-lg border border-slate-200 bg-white"
			style={{ gridTemplateColumns: "360px 1fr" }}
		>
			<DiscrepancyMaster
				cards={visibleCards}
				activeId={activeId}
				onActiveChange={setActiveId}
				pinnedIds={pinned.pinnedIds}
				onTogglePin={pinned.togglePinned}
				search={search}
				onSearchChange={setSearch}
				searchInputRef={searchInputRef}
			/>
			<div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50/40">
				<div className="sticky top-0 z-10 flex items-center justify-between border-slate-200 border-b bg-white px-4 py-2.5">
					<div className="flex min-w-0 items-center gap-2">
						{activeCard ? (
							<button
								type="button"
								onClick={() => pinned.togglePinned(activeCard.id)}
								aria-label={
									pinned.isPinned(activeCard.id)
										? messages.detail_unpin_aria
										: messages.detail_pin_aria
								}
								className={`rounded p-1 ${pinned.isPinned(activeCard.id) ? "text-amber-500" : "text-slate-400 hover:bg-slate-100 hover:text-amber-500"}`}
							>
								<Pin className="h-4 w-4" aria-hidden="true" />
							</button>
						) : null}
						<span className="font-mono text-[11px] text-slate-400">
							{formatMessage(messages.detail_position_template, {
								current: activeIndex >= 0 ? activeIndex + 1 : 0,
								total: orderedIds.length,
							})}
						</span>
						<span className="text-slate-300">·</span>
						{ruleLabel ? (
							<span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-700 uppercase tracking-wide">
								{ruleLabel}
							</span>
						) : null}
						{code ? <span className="font-mono text-[11px] text-slate-500">код {code}</span> : null}
					</div>
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={goPrev}
							disabled={activeIndex <= 0}
							aria-label={messages.detail_prev_aria}
							className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<kbd className="font-mono">K</kbd>{" "}
							<ChevronUp className="inline h-3 w-3" aria-hidden="true" />
						</button>
						<button
							type="button"
							onClick={goNext}
							disabled={activeIndex >= orderedIds.length - 1}
							aria-label={messages.detail_next_aria}
							className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<kbd className="font-mono">J</kbd>{" "}
							<ChevronDown className="inline h-3 w-3" aria-hidden="true" />
						</button>
						{activeCard ? (
							<a
								href={`#discrepancy-${activeCard.id}`}
								aria-label={messages.detail_open_aria}
								className="ml-2 rounded p-1 text-slate-400 hover:bg-slate-100"
							>
								<ExternalLink className="h-4 w-4" aria-hidden="true" />
							</a>
						) : null}
					</div>
				</div>
				<div ref={detailScrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
					{activeCard ? (
						<DiscrepancyCard viewModel={activeCard} runId={runId} onRelatedClick={onRelatedClick} />
					) : (
						<div className="rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center text-[12px] text-slate-500">
							{messages.master_empty_filter}
						</div>
					)}
					<div className="rounded border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-400">
						{messages.detail_footer_hint}
					</div>
				</div>
			</div>
		</div>
	);
}
